import { Link } from '@tanstack/react-router'
import { Loader2, Plus, Store } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import {
  useDeleteListing,
  useListings,
  usePublishListing,
} from '~/entities/listing/model/use-listings'
import { RenderedListingCard } from '~/features/listings/components/rendered-listing-card'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { useInfiniteScroll } from '~/shared/lib/use-infinite-scroll'
import { DESTRUCTIVE_OUTLINE_BUTTON } from '~/shared/styles/buttons'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { CardGridSkeleton } from '~/shared/ui/card-grid-skeleton'

type ConfirmationDialog = {
  type: 'publish' | 'delete'
  listingId: string
  listingName: string
  hasCoverImage?: boolean
}

export function ListingsSection() {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id || ''
  const { listings, total, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useListings(businessId)
  const publishListing = usePublishListing(businessId)
  const deleteListing = useDeleteListing(businessId)
  const [confirmDialog, setConfirmDialog] = useState<ConfirmationDialog | null>(null)

  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  })

  const handlePublish = (listingId: string, listingName: string, hasCoverImage: boolean) => {
    if (!hasCoverImage) {
      toast.error(m.cannotPublishListing(), {
        description: m.pleaseAddACoverImageBeforePublishing(),
      })
      return
    }
    setConfirmDialog({
      type: 'publish',
      listingId,
      listingName,
      hasCoverImage,
    })
  }

  const handleConfirm = async () => {
    if (!confirmDialog) return

    try {
      switch (confirmDialog.type) {
        case 'publish':
          await publishListing.mutateAsync(confirmDialog.listingId)
          toast.success(m.listingPublished(), {
            description: m.listingIsNowLiveAndVisibleToUsers({
              listingName: confirmDialog.listingName,
            }),
          })
          break
        case 'delete':
          await deleteListing.mutateAsync(confirmDialog.listingId)
          toast.success(m.listingDeleted(), {
            description: m.listingDeletedDesc({
              listingName: confirmDialog.listingName,
            }),
          })
          break
      }
      setConfirmDialog(null)
    } catch (error) {
      toast.error(getErrorMessageFromError(error))
    }
  }

  if (isLoading) {
    return <CardGridSkeleton keyPrefix="listing-card-skeleton" />
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <h2 className="mb-2 font-semibold text-destructive text-xl">{m.accessDenied1()}</h2>
          <p className="mb-4 text-center text-muted-foreground">
            {m.unableToLoadListingsThisAppearsToBeABackendAuthorizationIssue()}
            <br />
            {m.error()}: {error instanceof Error ? error.message : m.unknownError()}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <section className="space-y-3">
      <AlertDialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog?.type === 'publish' && m.publishListing()}
              {confirmDialog?.type === 'delete' &&
                m.confirmDeleteListing({
                  listingName: confirmDialog.listingName,
                })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog?.type === 'publish' && m.thisWillMakeTheListingVisibleToAllUsers()}
              {confirmDialog?.type === 'delete' && m.deleteListingWarning()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={confirmDialog?.type === 'delete' ? DESTRUCTIVE_OUTLINE_BUTTON : ''}
            >
              {confirmDialog?.type === 'publish' && m.publish()}
              {confirmDialog?.type === 'delete' && m.deleteListing()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {total === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Store className="mb-4 h-16 w-16 text-muted-foreground" />
            <h3 className="mb-2 font-semibold text-xl">{m.noListingsYet()}</h3>
            <p className="mb-4 text-center text-muted-foreground">
              {m.createYourFirstListingToShowcaseYourBusiness()}
            </p>
            <Link to="/setup/profile" search={{ kind: 'place' }}>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                {m.createListing()}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="auto-grid">
          {listings.map((listing) => (
            <RenderedListingCard
              key={listing.id}
              listing={listing}
              isPendingPublish={publishListing.isPending}
              onPublish={handlePublish}
            />
          ))}

          {total === 1 && (
            <Card className="group relative flex cursor-pointer flex-col overflow-hidden border-dashed bg-muted/30 transition-colors hover:bg-muted/50">
              <Link
                to="/setup/profile"
                search={{ kind: 'place' }}
                className="absolute inset-0 z-10"
              />
              <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden bg-muted/50">
                <Store className="h-12 w-12 text-muted-foreground/40" />
              </div>
              <CardHeader>
                <CardTitle className="text-muted-foreground/60">{m.additionalLocation()}</CardTitle>
                <CardDescription className="text-muted-foreground/50">
                  {m.createYourFirstListingToShowcaseYourBusiness()}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col">
                <div className="mt-auto flex gap-2 pt-4">
                  <Button variant="outline" size="sm" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    {m.addLocation()}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {hasNextPage && <div ref={sentinelRef} aria-hidden className="h-1" />}

      {isFetchingNextPage && (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
    </section>
  )
}
